import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Loader, Sparkles, X, Plus } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MD = {
  p: (p) => <p className="text-[13px] leading-relaxed my-2 first:mt-0 last:mb-0" {...p} />,
  strong: (p) => <strong className="text-[#1A4331] font-semibold" {...p} />,
  ul: (p) => <ul className="my-2 space-y-1 list-none pl-0" {...p} />,
  ol: (p) => <ol className="my-2 space-y-1 list-decimal pl-4" {...p} />,
  li: (p) => <li className="text-[13px] leading-relaxed pl-4 relative before:content-['•'] before:absolute before:left-0 before:text-[#6B9F4A] before:font-bold" {...p} />,
  h3: (p) => <h4 className="text-[#1A4331] font-bold text-[13px] mt-3 mb-1 uppercase tracking-wider" {...p} />,
  a: (p) => <a className="text-[#6B9F4A] underline" target="_blank" rel="noopener noreferrer" {...p} />,
};

// Right-side collapsible AI Coach dock (Today + Planner only). Reloads the most
// recent conversation from the backend so chat continuity survives navigation.
export default function CoachDock({ user, open, onClose }) {
  const firstName = user?.name?.split(' ')[0] || 'there';
  const greeting = {
    role: 'assistant',
    content: `**Hi ${firstName}** 🌱 I'm right here while you plan. Ask me what to sow, how to space a bed, or what needs doing today.`,
  };
  const [messages, setMessages] = useState([greeting]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // On mount, pull the newest conversation so the dock continues where you left off.
  useEffect(() => {
    let live = true;
    fetch(`${API}/ai/conversations`, { credentials: 'include' })
      .then((r) => r.json())
      .then(async (d) => {
        const newest = (d.conversations || [])[0];
        if (!newest || !live) return;
        const mr = await fetch(`${API}/ai/conversations/${newest.id}/messages`, { credentials: 'include' });
        const md = await mr.json();
        if (!live) return;
        setActiveConvId(newest.id);
        setMessages([greeting, ...(md.messages || []).map((m) => ({ role: m.role, content: m.content }))]);
      })
      .catch(() => {});
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const newConversation = () => {
    if (isLoading) return;
    setActiveConvId(null);
    setMessages([greeting]);
  };

  const send = async (text) => {
    if (!text.trim() || isLoading) return;
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setInput('');
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: text, conversation_id: activeConvId }),
      });
      const newConvId = response.headers.get('X-Conversation-Id');
      if (newConvId && newConvId !== activeConvId) setActiveConvId(newConvId);
      if (!response.ok || !response.body) throw new Error(`Chat failed (${response.status})`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistant = '';
      let buffer = '';
      setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop();
        for (const frame of frames) {
          if (!frame.startsWith('data: ')) continue;
          const payload = frame.slice(6);
          if (payload === '[DONE]') continue;
          try { assistant += JSON.parse(payload); } catch { assistant += payload; }
        }
        setMessages((prev) => {
          const u = [...prev];
          u[u.length - 1] = { role: 'assistant', content: assistant };
          return u;
        });
      }
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', content: 'Sorry, I had trouble responding just now. Please try again.' }]);
    } finally {
      setIsLoading(false);
    }
  };

  const submit = (e) => { e.preventDefault(); send(input); };

  return (
    <motion.aside
      data-testid="coach-dock"
      initial={false}
      animate={{ x: open ? 0 : '105%' }}
      transition={{ type: 'spring', stiffness: 260, damping: 30 }}
      className="hidden md:flex fixed right-0 top-0 bottom-0 z-40 w-[360px] hg-glass-dock flex-col shadow-[-20px_0_50px_-24px_rgba(26,67,49,0.5)]"
    >
      <div className="flex items-center justify-between px-5 pt-6 pb-4 border-b border-[#1A4331]/10">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-[#1A4331] flex items-center justify-center">
            <Sparkles className="w-[18px] h-[18px] text-[#C9A85B]" />
          </div>
          <div>
            <p className="text-sm font-bold text-[#1A4331] leading-none" style={{ fontFamily: 'Outfit, sans-serif' }}>Coach</p>
            <p className="text-[10px] text-[#7A6B57] uppercase tracking-wider mt-1">Command Center</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={newConversation} data-testid="coach-dock-new" title="New chat" className="hg-press w-9 h-9 rounded-full hover:bg-[#1A4331]/8 flex items-center justify-center text-[#5C665C]">
            <Plus className="w-[18px] h-[18px]" />
          </button>
          <button onClick={onClose} data-testid="coach-dock-close" title="Close" className="hg-press w-9 h-9 rounded-full hover:bg-[#1A4331]/8 flex items-center justify-center text-[#5C665C]">
            <X className="w-[18px] h-[18px]" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto hg-scroll px-4 py-4 space-y-4" data-testid="coach-dock-messages">
        {messages.map((m, idx) => (
          m.role === 'user' ? (
            <div key={idx} className="flex justify-end">
              <div className="max-w-[85%] bg-[#1A4331] text-[#FAF6EC] rounded-2xl rounded-br-md px-4 py-2.5">
                <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{m.content}</p>
              </div>
            </div>
          ) : (
            <div key={idx} className="flex justify-start">
              <div className="max-w-[92%] bg-white/70 border border-[#1A4331]/10 rounded-2xl rounded-tl-md px-4 py-3 text-[#2A3528]">
                <div className="prose prose-sm max-w-none">
                  <ReactMarkdown components={MD}>{m.content || '\u00A0'}</ReactMarkdown>
                </div>
              </div>
            </div>
          )
        ))}
        {isLoading && messages[messages.length - 1]?.role === 'user' && (
          <div className="flex items-center gap-2 bg-white/70 border border-[#1A4331]/10 rounded-2xl px-4 py-2.5 w-fit" data-testid="coach-dock-typing">
            <Loader className="w-4 h-4 text-[#6B9F4A] animate-spin" />
            <span className="text-xs text-[#7A6B57]">Reading your garden…</span>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={submit} className="border-t border-[#1A4331]/10 p-3 bg-white/40">
        <div className="flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask your coach…"
            disabled={isLoading}
            data-testid="coach-dock-input"
            className="flex-1 px-4 py-2.5 bg-white border border-[#1A4331]/10 focus:border-[#1A4331] rounded-full outline-none transition-all text-[13px] disabled:opacity-50"
          />
          <button type="submit" disabled={isLoading || !input.trim()} data-testid="coach-dock-send"
            className="hg-press bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] p-2.5 rounded-full transition-colors disabled:opacity-50 flex-shrink-0">
            {isLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </form>
    </motion.aside>
  );
}
